import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Context } from "../../../../../context";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { BaseAlertModal } from "../../../../../components/modals/alert";
import { Layout } from "../../../../../containers/layout/dashboard";
import { EditSMSAlertWrapper } from "./styled";
import { BaseButton } from "../../../../../components/button/styled";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { retrieveInstitutionsService } from "../../../../../utils/apis/institution/retrieveInstitutions";
import { BaseFieldSet } from "../../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../../components/formfields/label/styled";
import { BaseSelect } from "../../../../../components/formfields/select/styled";
import { BaseOption } from "../../../../../components/formfields/option/styled";
import { BaseInput } from "../../../../../components/formfields/input/styled";
import { retrieveSMSAlertByIdService } from "../../../../../utils/apis/configuration/smsalert/retrieveSMSAlertById";
import { updateSMSAlertService } from "../../../../../utils/apis/configuration/smsalert/updateSMSAlert";
import { useQuery } from "@tanstack/react-query";

export const EditSMSAlert = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { setActiveNavItem } = useContext(Context);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [formDetails, setFormDetails] = useState({
        senderId: "",
        institution: " ",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    const { data: institutions } = useQuery({
        queryKey: ['institutions', TOKEN],
        queryFn: async () => retrieveInstitutionsService(TOKEN),
        enabled: !!TOKEN,
    });

    useEffect(() => {
        const fetchSMSAlert = async () => {
            try {
                const data = await retrieveSMSAlertByIdService(TOKEN, id);
                setFormDetails(data);
            } catch (err) {
                console.error("Failed to fetch SMS Alert:", err);
            }
        }
        fetchSMSAlert();
    }, [TOKEN, id]);

    const handleNavigateToPrevious = () => {
        return navigate(-1);
    }

    const handleAlertModalPersist = () => {
        return setOpenAlertModal(true);
    }

    const handleNavigateToSMSAlert = () => {
        navigate("/configuration/smsalert");
        return setOpenAlertModal(false);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await updateSMSAlertService(formDetails, id, TOKEN);
            if (response.status === "success") {
                setIsLoading(false);
                setOpenAlertModal(true);
            } else {
                setIsLoading(false);
                setError('Failed to edit user. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to edit user. ${error.message}`);
            console.error('Failed to edit user:', error);
        }
    }

    return (
        <Layout
            id="edit-sms-alert"
        >
            <EditSMSAlertWrapper>
                <BaseAlertModal
                    title="Success"
                    message="SMS Alert successfully edited"
                    open={openAlertModal}
                    handleClose={handleAlertModalPersist}
                    callToAction="Continue"
                    handleCallToAction={handleNavigateToSMSAlert}
                />
                <Box
                    component={"div"}
                    className="title-box"
                >
                    <Typography
                        variant="h1"
                        fontFamily={"Inter"}
                        fontWeight={700}
                        fontSize={24}
                        lineHeight={"normal"}
                        color={"#373737"}
                        marginBlockEnd={"calc(var(--basic-margin)/4)"}
                    >
                        Edit SMS Alert
                    </Typography>
                    <BaseButton
                        variant="outlined"
                        border="1px solid #D7D7D7"
                        colour="#373737"
                        bgcolor="#FFFFFF"
                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                        fontSize={12}
                        fontWeight={800}
                        startIcon={<ArrowLeftIcon />}
                        onClick={handleNavigateToPrevious}
                    >
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Back
                        </Typography>
                    </BaseButton>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Name of MFI
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="institution"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.institution}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select institution
                                    </BaseOption>
                                    {institutions?.map((institution: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={institution.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {institution.name}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Sender ID
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="senderId"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter Sender ID"
                                    value={formDetails.senderId}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={16}
                        lineHeight={"normal"}
                        color={"#FF0000"}
                        whiteSpace={"normal"}
                        marginBlockStart={"var(--basic-margin)"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        component={"div"}
                        className="button-box"
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            radius="7px"
                            fontSize={16}
                            fontWeight={800}
                            bgcolor="#791377"
                            padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Update
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </EditSMSAlertWrapper>
        </Layout>
    )
}